'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll reveal logic
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -60px 0px',
      }
    );

    const elements = document.querySelectorAll('.fade-in-up.will-animate');
    elements?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, [pathname]);

  const navLinks = [
    { id: 'nav_home', label: 'Home', href: '/homepage' },
    { id: 'nav_services', label: 'Leistungen', href: '/services' },
    { id: 'nav_contact', label: 'Kontakt', href: '/contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-primary/80 backdrop-blur-luxury py-4' :'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        {/* Logo */}
        <Link
          href="/homepage"
          className={`font-serif text-2xl font-light tracking-wide transition-colors ${
            isScrolled ? 'text-white' : 'text-primary'
          }`}
        >
          KMW Consulting
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-12">
          {navLinks?.map((link) => (
            <Link
              key={link?.id}
              href={link?.href}
              className={`nav-link text-[10px] font-bold uppercase tracking-ultra transition-colors ${
                pathname === link?.href
                  ? 'text-accent active'
                  : isScrolled
                  ? 'text-white/80 hover:text-white' :'text-foreground/80 hover:text-foreground'
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </div>

        {/* CTA Button */}
        <Link
          href="/contact"
          className={`hidden md:block px-8 py-3 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all ${
            isScrolled
              ? 'bg-accent text-accent-foreground hover:bg-accent/90'
              : 'bg-primary text-primary-foreground hover:bg-primary/90'
          }`}
        >
          Kontakt
        </Link>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden p-3 rounded-full bg-primary/10 backdrop-blur-md"
          aria-label="Toggle menu"
        >
          <Icon
            name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
            size={24}
            className={isScrolled ? 'text-white' : 'text-primary'}
          />
        </button>
      </div>
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-primary/95 backdrop-blur-luxury py-8">
          <div className="flex flex-col items-center gap-6">
            {navLinks?.map((link) => (
              <Link
                key={link?.id}
                href={link?.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`nav-link text-[10px] font-bold uppercase tracking-ultra ${
                  pathname === link?.href
                    ? 'text-accent active' :'text-white/80 hover:text-white'
                }`}
              >
                {link?.label}
              </Link>
            ))}
            <Link
              href="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="mt-4 px-8 py-3 rounded-full text-[10px] font-bold uppercase tracking-widest bg-accent text-accent-foreground hover:bg-accent/90 transition-all"
            >
              Kontakt
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}